import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  RefreshCw,
  Search,
  Loader2,
  Code2,
  Wrench,
  FlaskConical,
  Pencil,
  Trash2,
  ChevronLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { unifiedFunctionsApi, type UnifiedFunction } from '@/services/unifiedFunctionsApi';

const DEFAULT_TOOL_CODE = `def execute(params: dict, context: dict) -> dict:
    """
    Execute the tool.

    Args:
        params: Input parameters from the agent's tool call
        context: Execution context with mode='tool'

    Returns:
        Result dictionary to return to the agent
    """
    # Example: access a parameter
    # value = params.get("input_value")

    return {"result": "Hello from tool!"}
`;

const DEFAULT_EVAL_CODE = `def execute(params: dict, context: dict) -> dict:
    """
    Evaluate agent output.

    Args:
        params: Empty dict (not used in eval mode)
        context: Contains eval_context with:
            - input_data: Input provided to the agent
            - output_data: Output produced by the agent
            - expected: Expected output (if provided)
            - trajectory_id: Trajectory ID

    Returns:
        Dict with: passed (bool), score (float 0-1), details (dict)
    """
    eval_ctx = context.get("eval_context", {})
    output_data = eval_ctx.get("output_data", {})
    expected = eval_ctx.get("expected", {})

    # Example: check if output contains expected value
    output_str = str(output_data.get("output", ""))
    expected_str = str(expected.get("value", ""))

    passed = expected_str.lower() in output_str.lower()

    return {
        "passed": passed,
        "score": 1.0 if passed else 0.0,
        "details": {"output_length": len(output_str)}
    }
`;

export function FunctionsListPage() {
  const navigate = useNavigate();
  const [functions, setFunctions] = useState<UnifiedFunction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [functionToDelete, setFunctionToDelete] = useState<UnifiedFunction | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [creating, setCreating] = useState(false);

  // Create form state
  const [newFunctionName, setNewFunctionName] = useState('');
  const [newFunctionType, setNewFunctionType] = useState<'tool' | 'eval'>('tool');

  const fetchFunctions = async () => {
    try {
      setLoading(true);
      const data = await unifiedFunctionsApi.list();
      setFunctions(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch functions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFunctions();
  }, []);

  const filteredFunctions = functions.filter((fn) => {
    const matchesSearch =
      !searchQuery ||
      fn.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      fn.description?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType =
      typeFilter === 'all' ||
      (typeFilter === 'tool' && fn.is_tool) ||
      (typeFilter === 'eval' && fn.is_eval_function) ||
      (typeFilter === 'builtin' && fn.is_builtin) ||
      (typeFilter === 'user' && !fn.is_builtin);

    return matchesSearch && matchesType;
  });

  // Separate built-in and user-defined functions
  const builtinFunctions = filteredFunctions.filter((fn) => fn.is_builtin);
  const userFunctions = filteredFunctions.filter((fn) => !fn.is_builtin);

  const handleDelete = async () => {
    if (!functionToDelete) return;
    try {
      setDeleting(true);
      await unifiedFunctionsApi.delete(functionToDelete.id);
      setDeleteDialogOpen(false);
      setFunctionToDelete(null);
      fetchFunctions();
    } catch (err) {
      setError(`Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreate = async () => {
    if (!newFunctionName.trim()) return;

    const defaultCode = newFunctionType === 'tool' ? DEFAULT_TOOL_CODE : DEFAULT_EVAL_CODE;

    try {
      setCreating(true);
      const fn = await unifiedFunctionsApi.create({
        name: newFunctionName.trim(),
        code: defaultCode,
        is_tool: newFunctionType === 'tool',
        is_eval_function: newFunctionType === 'eval',
      });
      setCreateDialogOpen(false);
      setNewFunctionName('');
      setNewFunctionType('tool');
      navigate(`/developer/functions/${fn.id}`);
    } catch (err) {
      setError(`Failed to create: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setCreating(false);
    }
  };

  const renderFunctionRow = (fn: UnifiedFunction) => {
    return (
      <TableRow key={fn.id} className="cursor-pointer hover:bg-muted/50">
        <TableCell onClick={() => navigate(`/developer/functions/${fn.id}`)}>
          <div className="flex items-center gap-2">
            <Code2 className="h-4 w-4 text-primary" />
            <div>
              <p className="font-medium">{fn.display_name || fn.name}</p>
              {fn.description && (
                <p className="text-sm text-muted-foreground truncate max-w-[400px]">
                  {fn.description}
                </p>
              )}
            </div>
          </div>
        </TableCell>
        <TableCell onClick={() => navigate(`/developer/functions/${fn.id}`)}>
          <div className="flex gap-1 flex-wrap">
            {fn.is_builtin && (
              <Badge variant="secondary">
                Built-in
              </Badge>
            )}
            {fn.is_tool && (
              <Badge variant="outline" className="text-blue-600 border-blue-600">
                <Wrench className="h-3 w-3 mr-1" />
                Tool
              </Badge>
            )}
            {fn.is_eval_function && (
              <Badge variant="outline" className="text-green-600 border-green-600">
                <FlaskConical className="h-3 w-3 mr-1" />
                Eval
              </Badge>
            )}
          </div>
        </TableCell>
        <TableCell onClick={() => navigate(`/developer/functions/${fn.id}`)}>
          <span className="text-sm text-muted-foreground">
            {fn.usage_count} uses
          </span>
        </TableCell>
        <TableCell className="text-right">
          {!fn.is_builtin && (
            <div className="flex justify-end gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/developer/functions/${fn.id}`);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit Function</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFunctionToDelete(fn);
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Delete Function</TooltipContent>
              </Tooltip>
            </div>
          )}
        </TableCell>
      </TableRow>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container max-w-6xl py-8">
      <div className="flex items-center gap-2 mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate('/developer')}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          Developer
        </Button>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Functions</h1>
        <div className="flex gap-2">
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Function
          </Button>
          <Button variant="outline" onClick={fetchFunctions}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search functions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="tool">Tools Only</SelectItem>
            <SelectItem value="eval">Eval Only</SelectItem>
            <SelectItem value="builtin">Built-in Only</SelectItem>
            <SelectItem value="user">User-Defined Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Usage</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredFunctions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">
                  <p className="text-muted-foreground">
                    {searchQuery || typeFilter !== 'all'
                      ? 'No functions match your filters'
                      : 'No functions found. Create one to get started.'}
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {/* User-defined functions first */}
                {userFunctions.length > 0 && (
                  <>
                    <TableRow className="bg-muted/50">
                      <TableCell colSpan={4} className="py-2">
                        <span className="text-sm font-medium text-muted-foreground">
                          User-Defined Functions ({userFunctions.length})
                        </span>
                      </TableCell>
                    </TableRow>
                    {userFunctions.map(renderFunctionRow)}
                  </>
                )}

                {/* Built-in functions */}
                {builtinFunctions.length > 0 && (
                  <>
                    <TableRow className="bg-muted/50">
                      <TableCell colSpan={4} className="py-2">
                        <span className="text-sm font-medium text-muted-foreground">
                          Built-in Functions ({builtinFunctions.length})
                        </span>
                      </TableCell>
                    </TableRow>
                    {builtinFunctions.map(renderFunctionRow)}
                  </>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Function</DialogTitle>
            <DialogDescription>
              Create a new function to use as an agent tool or for evaluations.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="my_function"
                value={newFunctionName}
                onChange={(e) => setNewFunctionName(e.target.value)}
              />
            </div>
            <div className="space-y-3">
              <Label>Function Type</Label>
              <RadioGroup
                value={newFunctionType}
                onValueChange={(value) => setNewFunctionType(value as 'tool' | 'eval')}
                className="flex flex-col gap-3"
              >
                <div className="flex items-center space-x-3 rounded-md border p-3 cursor-pointer hover:bg-muted/50">
                  <RadioGroupItem value="tool" id="type-tool" />
                  <Label htmlFor="type-tool" className="cursor-pointer flex-1">
                    <div className="flex items-center gap-2">
                      <Wrench className="h-4 w-4 text-blue-600" />
                      <span className="font-medium">Agent Tool</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Called by agents during execution to perform actions
                    </p>
                  </Label>
                </div>
                <div className="flex items-center space-x-3 rounded-md border p-3 cursor-pointer hover:bg-muted/50">
                  <RadioGroupItem value="eval" id="type-eval" />
                  <Label htmlFor="type-eval" className="cursor-pointer flex-1">
                    <div className="flex items-center gap-2">
                      <FlaskConical className="h-4 w-4 text-green-600" />
                      <span className="font-medium">Eval Function</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Evaluates agent output against expected results
                    </p>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating || !newFunctionName.trim()}>
              {creating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Function</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{functionToDelete?.name}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default FunctionsListPage;
